import React, { memo, useRef, useEffect, useState, useCallback } from "react";
import { dates, formats, validators } from "investira.sdk";
import { Stack, Card, Icon, Typography } from "investira.react.components";
import { HighchartsReact } from "investira.react.charts";
import Controls from "../AtivoCharts/Controls";

const AtivoRentabilidade = memo((props) => {
  const { cotacoes, isIndicadorLoading } = props;
  const chartRef = useRef(null);

  function formatData(pData = []) {
    if (validators.isNull(pData)) {
      return [];
    }
    const xAttr = ["rentabilidade"];

    const xFormattedData = pData.map((xPoint) => {
      const xDate = dates.toDate(xPoint.data).getTime();
      const xType = xAttr[0];
      return [xDate, xPoint[xType]];
    });

    return xFormattedData;
  }

  const createChartOptions = useCallback((pData, pRange = "full") => {
    const xSeries = pData.map((pCotacao) => {
      const xData = pCotacao.data[pRange];

      return {
        name: pCotacao.ativo,
        type: "line",
        data: formatData(xData),
      };
    });

    const xOptions = {
      title: false,
      chart: {
        zooming: {
          type: "x",
        },
        type: "spline",
      },
      rangeSelector: {
        enabled: false,
        buttons: [
          {
            type: "month",
            count: 1,
            text: "1M",
          },
          {
            type: "month",
            count: 6,
            text: "6M",
          },
          {
            type: "year",
            count: 1,
            text: "1A",
          },
          {
            type: "year",
            count: 2,
            text: "2A",
          },
          {
            type: "year",
            count: 3,
            text: "3A",
          },
          {
            type: "year",
            count: 5,
            text: "5A",
          },
          {
            type: "ytd",
            text: "YTD",
          },
          {
            type: "all",
            text: "MÁX.",
          },
        ],
        selected: 2,
      },
      xAxis: { type: "datetime", crosshair: true },
      yAxis: {
        type: "linear",
        title: false,
        labels: {
          formatter: function () {
            return `${this.value}%`;
          },
        },
      },
      series: xSeries,
      tooltip: {
        crosshairs: true,
        shared: true,
        formatter: function () {
          const xDate = formats.formatDateCustom(this.point.x, "DD/MMM/YYYY");

          let xTooltipContent = `<span style="font-size: 1em"><b>${xDate}</b></span><br/>`;

          this.points.forEach(function (point) {
            const xRentabilidade = formats.formatNumber(point.y, 2, false);
            xTooltipContent += `<span style="color:${point.color}">\u25CF</span>${point.series.name}: <b>${xRentabilidade}%</b><br/>`;
          });

          return xTooltipContent;
        },
      },
    };

    return xOptions;
  }, []);

  const [range, setRange] = useState(2);
  const [indicador, setIndicador] = useState("IBOV");
  const [options, setOptions] = useState(null);

  const handleRangeSelect = (_pType, _pCount, pIndex) => {
    setRange(pIndex);
  };

  useEffect(() => {
    const xRange = [
      "umMes",
      "seisMeses",
      "umAno",
      "doisAnos",
      "tresAnos",
      "cincoAnos",
      "ytd",
      "full",
    ];

    if (!validators.isNull(cotacoes[1]?.data)) {
      const xOptions = createChartOptions(cotacoes, xRange[range]);

      setOptions(xOptions);
    }
  }, [cotacoes, indicador, range, createChartOptions]);

  if (validators.isEmpty(cotacoes[0].data.full)) {
    return null;
  }

  if (validators.isNull(options)) {
    return null;
  }

  return (
    <Stack>
      <Card>
        <Stack>
          <Stack
            flexGrow={1}
            py={2}
            px={2}
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            {/* <Stack>
              <Icon iconName="chart_line" color="primary" size={21} />
            </Stack> */}
            <Stack flexGrow="1">
              <Typography
                variant="subtitle1"
                color="textPrimary"
                sx={{ fontWeight: 600 }}
                noWrap
              >
                Rentabilidade Comparada
              </Typography>
            </Stack>
            <Controls
              range={{
                selected: range,
                buttons: options.rangeSelector.buttons,
                setRange: handleRangeSelect,
              }}
              indicador={{
                value: indicador,
                onChange: (pValue) => {
                  setIndicador(pValue);
                  props.actions.handleIndicadorChange({
                    current: pValue,
                    historico: [],
                  });
                },
              }}
            />
          </Stack>
          <Stack sx={{ px: 2, py: 2 }}>
            <HighchartsReact
              key={"cotacao"}
              ref={chartRef}
              options={options}
              isLoading={isIndicadorLoading}
            />
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
});

export default AtivoRentabilidade;
